import { APP_CONSTANTS } from '../../config/miaw/constants';

// storageKey holds top level storage key in the browser storage.
export let storageKey: string;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let storageName: string;

/**
 * True if this browser session allows access to local storage. Some users may have stricter browser security settings. Do nothing on error.
 */
export const isLocalStorageAvailable = () => {
  try {
    if (typeof window === 'undefined') {
      console.warn('Local storage not available in this environment.');
      return false;
    }
    // Test getting an item to see if this triggers an error
    window?.localStorage?.getItem('test');

    if (window?.localStorage && typeof window?.localStorage === 'object') {
      storageName = 'localStorage';
      return true;
    }
  } catch {
    // localStorage is not available. User chat sessions continue only in a single-page view and not across multiple pages.
    console.log('localStorage not available.');
    return false;
  }
  // We should never reach this return statement, it exists to make the linter happy.
  return false;

  /**
   * localStorage data spans across tabs in a browser and sessionStorage is unique per tab.
   * Currently hardcoded to disable localStorage internally in the app by returning FALSE.
   * If the app's support (e.g. Messaging Session Continuation) is required to be enabled across tabs in a browser, uncomment the code block above and remove the hardcoded 'return false' statement.
   */
  return false;
};

/**
 * True if this browser session allows access to session storage. Some users may have stricter browser security settings. Do nothing on error.
 */
export const isSessionStorageAvailable = () => {
  try {
    if (typeof window === 'undefined') {
      console.warn('Session storage not available in this environment.');
      return false;
    }
    // Test getting an item to see if this triggers an error
    window?.sessionStorage.getItem('test');

    if (window?.sessionStorage && typeof window?.sessionStorage === 'object') {
      storageName = 'sessionStorage';
      return true;
    }
  } catch {
    // sessionStorage is not available. User chat sessions end after a web page refresh or across browser tabs and windows.
    console.log('sessionStorage not available.');
    return false;
  }
  // We should never reach this return statement, it exists to make the linter happy.
  return false;
};

/**
 * Determine the type of web storage (local vs. session) to be used
 * It will prioritize localStorage if specified, otherwise sessionStorage
 */
export const determineStorageType = (inLocalStorage = false) => {
  if (typeof window === 'undefined') {
    console.warn('Web storage determination skipped in this environment.');
    return undefined;
  }
  return isLocalStorageAvailable() && inLocalStorage ? window.localStorage : isSessionStorageAvailable() ? window.sessionStorage : undefined;
};

/**
 * Get item from web storage object payload.
 *
 * @param payload - Storage object as string
 * @param key - Storage Key
 * @returns {*} - Item if found, undefined otherwise.
 */
export const getItemInPayloadByKey = (payload: string, key: string) => {
  let item;

  if (payload) {
    const storageObj = JSON.parse(payload) || {};
    item = storageObj[key];
  }

  return item;
};

/**
 * Initialize Browser Web Storage (i.e. localStorage and/or sessionStorage) with a storage key including the Salesforce Organization Id.
 */
export const initializeWebStorage = (organizationId: string) => {
  if (typeof window === 'undefined') {
    console.warn('Web storage initialization skipped in this environment.');
    return;
  }
  storageKey = `${APP_CONSTANTS.WEB_STORAGE_KEY}${organizationId}`;
  const storageObj = JSON.stringify({});

  // Initialize the web storage object
  if (isLocalStorageAvailable() && !localStorage.getItem(storageKey)) {
    localStorage.setItem(storageKey, storageObj);
  }
  if (isSessionStorageAvailable() && !sessionStorage.getItem(storageKey)) {
    window?.sessionStorage?.setItem(storageKey, storageObj);
  }

  console.log('Browser Web Storage initialized.');
};

/**
 * Returns the item in web storage by the key in this current conversation.
 * It prioritizes getting the object in localStorage if exists in both
 * Returns undefined if not found
 */
export const getItemInWebStorageByKey = (key: string, inLocalStorage = true) => {
  if (typeof window === 'undefined') {
    console.warn('Web storage retrieval skipped in this environment.');
    return undefined;
  }
  let item;
  const storage = determineStorageType(inLocalStorage);

  if (storage) {
    const storageItem = storage.getItem(storageKey);
    const storageObj = (storageItem && JSON.parse(storageItem)) || {};
    item = storageObj[key];
  }
  return item;
};

/**
 * Set the item in web storage by the key in this current conversation.
 * If inLocalStorage is true, then first try to store in localStorage, otherwise sessionStorage
 */
export const setItemInWebStorage = (key: string, value: string, inLocalStorage = true) => {
  if (typeof window === 'undefined') {
    console.warn('Web storage setting skipped in this environment.');
    return;
  }
  const storage = determineStorageType(inLocalStorage);

  if (storage) {
    const storageItem = storage.getItem(storageKey);

    const storageObj = (storageItem && JSON.parse(storageItem)) || {};
    storageObj[key] = value;
    storage.setItem(storageKey, JSON.stringify(storageObj));
  }
};

/**
 * Remove item from both localStorage and sessionStorage that match that given key
 * As well as item that was originally stored in fallback location
 */
export const removeItemInWebStorage = (key: string) => {
  if (typeof window === 'undefined') {
    console.warn('Web storage removal skipped in this environment.');
    return;
  }
  const localStorageItem = localStorage.getItem(storageKey);
  if (isLocalStorageAvailable() && localStorageItem) {
    const storageObj = JSON.parse(localStorageItem) || {};
    delete storageObj[key];
    localStorage.setItem(storageKey, JSON.stringify(storageObj));
  }
  const sessionStorageItem = window?.sessionStorage?.getItem(storageKey);
  if (isSessionStorageAvailable() && sessionStorageItem) {
    const storageObj = JSON.parse(sessionStorageItem) || {};
    delete storageObj[key];
    window?.sessionStorage?.setItem(storageKey, JSON.stringify(storageObj));
  }
};

/**
 * Clear all client side stored item in both localStorage & sessionStorage
 */
export const clearWebStorage = () => {
  if (typeof window === 'undefined') {
    console.warn('Web storage clearing skipped in this environment.');
    return;
  }
  if (isLocalStorageAvailable()) {
    localStorage.removeItem(storageKey);
  }
  if (isSessionStorageAvailable()) {
    window?.sessionStorage?.removeItem(storageKey);
  }
  console.log('web storage cleared');
};
